import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowUpRight,
    Hammer,
    Layers,
    Users,
    Compass,
    ShieldCheck,
    Award,
    Clock,
    Wallet,
} from "lucide-react";
import HeroEmailCapture from "../components/HeroEmailCapture";
import QuoteForm from "../components/QuoteForm";
import {
    HERO_IMAGE,
    STATS,
    THREE_CS,
    FOUR_PILLARS,
    SECTORS,
    CLIENT_LOGOS,
    PRECON_SERVICES,
    CONSTRUCTION_SERVICES,
    DELIVERY_METHODS,
    FEATURED_PROJECTS,
    COMPANY,
} from "../lib/data";

const PILLAR_ICONS = {
    Safety: <ShieldCheck className="w-7 h-7" />,
    Quality: <Award className="w-7 h-7" />,
    Time: <Clock className="w-7 h-7" />,
    Cost: <Wallet className="w-7 h-7" />,
};

const Home = () => {
    const [projects, setProjects] = useState(FEATURED_PROJECTS);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects`).then((r) => {
            if (Array.isArray(r.data) && r.data.length > 0) setProjects(r.data);
        }).catch(() => { /* fall back to static */ });
    }, []);

    // One representative project per sector — prefer projects with real MWC photography
    const galleryProjects = SECTORS
        .map((sector) => {
            const withPhotos = projects.find(
                (p) => p.sector === sector.slug && p.images[0]?.startsWith("/img/projects/")
            );
            return withPhotos || projects.find((p) => p.sector === sector.slug);
        })
        .filter(Boolean);

    return (
        <main data-testid="home-page" className="bg-background">
            {/* HERO — MWC logo image only */}
            <section
                data-testid="hero-section"
                className="relative min-h-[70vh] md:min-h-[80vh] overflow-hidden bg-foreground"
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMAGE}
                        alt="Mark Wilson Construction projects collage"
                        className="w-full h-full object-contain scale-[1.35] md:object-cover md:scale-110 origin-center"
                    />
                </div>
            </section>

            {/* HEADLINE + EMAIL CAPTURE — below the hero, under the MWC logo */}
            <section
                data-testid="hero-content"
                className="relative bg-background border-b border-border"
            >
                <div className="px-6 md:px-12 lg:px-24 py-16 md:py-20">
                    <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end">
                        <div className="md:col-span-8 reveal">
                            <p
                                data-testid="builder-manifesto"
                                className="max-w-3xl text-sm sm:text-base lg:text-lg text-foreground/80 font-medium leading-relaxed text-justify mb-8"
                            >
                                To be a builder means to build trust, consensus, relationships,
                                sustainability, discipline, confidence, knowledge, and health. The
                                buildings we build will stand as a reflection of our career — but
                                what we do for the people around us will outlast every structure.
                            </p>
                            <div className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-accent font-bold mt-8 flex items-center gap-3">
                                <span className="w-8 h-px bg-accent" />
                                Commercial builder · Fresno, CA · Since {COMPANY.founded}
                            </div>
                            <p className="mt-6 max-w-3xl text-sm sm:text-base text-foreground/80 leading-relaxed text-justify">
                                Creating more than buildings. We build lasting relationships, earn
                                trust, and invest in the next generation of craftspeople. Every
                                project reflects the people, teamwork, and dedication behind it.
                            </p>
                        </div>

                        <div className="md:col-span-4 reveal" style={{ animationDelay: "150ms" }}>
                            <div className="border border-foreground/15 bg-card p-6 max-w-md md:ml-auto">
                                <div className="font-display font-black uppercase tracking-tight text-2xl leading-tight text-foreground">
                                    Sign Up to Bid
                                </div>
                                <p className="mt-2 text-sm text-muted-foreground">
                                    Drop your email and we'll add you to our bid list.
                                </p>
                                <div className="mt-4">
                                    <HeroEmailCapture id="hero-email-capture" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section data-testid="trust-strip" className="border-y border-border bg-secondary text-secondary-foreground overflow-hidden">
                <div className="flex marquee whitespace-nowrap py-5">
                    {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
                        <span
                            key={i}
                            className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase mx-8 text-secondary-foreground/70"
                        >
                            {logo}
                            <span className="ml-8 text-accent">/</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* STATS — 3 CLICKABLE BOXES */}
            <section data-testid="stats-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="grid md:grid-cols-12 gap-6">
                    <div className="md:col-span-4">
                        <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                            01 / The Record
                        </div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9]">
                            Three decades.<br />
                            One way to build.
                        </h2>
                        <p className="mt-6 text-muted-foreground max-w-sm">
                            How we build is shaped by process, relationships, experience, and
                            consistency — refined over three decades and applied to every project,
                            regardless of size.
                        </p>
                    </div>
                    <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-background border border-border">
                        {STATS.map((s) => (
                            <Link
                                to={s.href}
                                key={s.label}
                                data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                                className="group bg-card p-6 md:p-8 hover:bg-accent hover:text-accent-foreground transition-colors block"
                            >
                                <div className="font-display font-black text-5xl sm:text-6xl tracking-tight text-foreground group-hover:text-accent-foreground">
                                    {s.value}
                                </div>
                                <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground group-hover:text-accent-foreground/80 flex items-center justify-between">
                                    <span>{s.label}</span>
                                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* THREE C's */}
            <section
                data-testid="three-cs-section"
                className="bg-foreground text-background border-y border-border"
            >
                <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                    <div className="grid md:grid-cols-12 gap-8 mb-16">
                        <div className="md:col-span-5">
                            <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                                02 / The Three C's
                            </div>
                            <h2 className="font-display font-black text-4xl sm:text-6xl uppercase tracking-tight leading-[0.85]">
                                Connect.
                                <br />
                                Communicate.
                                <br />
                                <span className="text-accent">Collaborate.</span>
                            </h2>
                        </div>
                        <p className="md:col-span-6 md:col-start-7 self-end text-background/70 text-lg leading-relaxed">
                            These three principles create the process by which we understand one
                            another, solve problems together, and ultimately build trust.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-px bg-background/15 border border-background/15">
                        {THREE_CS.map((c, i) => (
                            <div
                                key={c.title}
                                data-testid={`three-c-${c.title.toLowerCase()}`}
                                className="bg-foreground p-8 md:p-10 cursor-default select-none"
                            >
                                <div className="font-mono text-xs text-accent">
                                    0{i + 1} / 03
                                </div>
                                <div className="font-display font-black text-7xl text-accent mt-2 leading-none">
                                    {c.letter}
                                </div>
                                <h3 className="font-display font-black text-2xl uppercase tracking-tight mt-4">
                                    {c.title}
                                </h3>
                                <p className="mt-3 text-background/75 leading-relaxed">{c.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES BENTO */}
            <section
                data-testid="services-section"
                className="bg-secondary text-secondary-foreground"
            >
                <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                    <div className="grid md:grid-cols-12 gap-8 mb-16">
                        <div className="md:col-span-5">
                            <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                                03 / Capabilities
                            </div>
                            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9]">
                                From concept
                                <br /> to completion.
                            </h2>
                        </div>
                        <p className="md:col-span-6 md:col-start-7 text-secondary-foreground/70 text-lg leading-relaxed self-end">
                            We have established a strong record across multiple delivery methods
                            to suit your project's needs. The Listen. Plan. Build. methodology
                            elevates MWC above the standard services provided in the industry.
                        </p>
                    </div>

                    {/* Delivery Methods — moved ABOVE Preconstruction */}
                    <div className="mb-px bg-secondary-foreground/10 border border-secondary-foreground/15">
                        <div className="bg-secondary p-8 md:p-12">
                            <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-6">
                                Delivery Methods
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {DELIVERY_METHODS.map((m) => (
                                    <span
                                        key={m}
                                        data-testid={`delivery-tag-${m.replace(/\s+/g, "-").toLowerCase()}`}
                                        className="border border-secondary-foreground/20 px-4 py-2 text-sm uppercase tracking-wider cursor-default"
                                    >
                                        {m}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-12 gap-px bg-secondary-foreground/10 border border-secondary-foreground/15 mt-px">
                        <ServiceCard
                            span="md:col-span-7"
                            icon={<Compass className="w-7 h-7" />}
                            title="Preconstruction"
                            list={PRECON_SERVICES}
                            tag="Listen / Plan"
                        />
                        <ServiceCard
                            span="md:col-span-5"
                            icon={<Hammer className="w-7 h-7" />}
                            title="Construction"
                            list={CONSTRUCTION_SERVICES.map((c) => ({ title: c, outcome: null }))}
                            tag="Build"
                        />
                    </div>
                </div>
            </section>

            {/* FOUR PILLARS */}
            <section data-testid="four-pillars-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                <div className="grid md:grid-cols-12 gap-8 mb-16">
                    <div className="md:col-span-6">
                        <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                            04 / Pillars
                        </div>
                        <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight leading-[0.9]">
                            Four pillars.
                            <br />
                            <span className="text-accent">of a builder.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 md:col-start-8 self-end text-muted-foreground text-lg leading-relaxed">
                        These four pillars encompass everything that can relate to a project. They
                        hold us accountable, keep us focused throughout decision making, and help us
                        maintain balance from kickoff to close-out.
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-background border border-border">
                    {FOUR_PILLARS.map((p) => (
                        <div
                            key={p.title}
                            data-testid={`pillar-${p.title.toLowerCase()}`}
                            className="bg-card p-8 group"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <span className="text-accent">{PILLAR_ICONS[p.title]}</span>
                                <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted-foreground">
                                    {p.n}
                                </span>
                            </div>
                            <div className="font-display font-black text-3xl uppercase tracking-tight">
                                {p.title}
                            </div>
                            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                                {p.body}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* PROJECTS GALLERY (Sectors) */}
            <section
                data-testid="projects-gallery"
                className="bg-foreground text-background"
            >
                <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                        <div>
                            <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                                05 / Selected Work
                            </div>
                            <h2 className="font-display font-black text-4xl sm:text-6xl uppercase tracking-tight leading-[0.9]">
                                Market sectors we build.
                            </h2>
                        </div>
                        <Link
                            to="/projects"
                            data-testid="view-all-projects"
                            className="self-start md:self-end inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider hover:text-accent transition-colors"
                        >
                            View full portfolio
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-background/10">
                        {galleryProjects.map((p, i) => (
                            <GalleryCard key={p.id} project={p} index={i} />
                        ))}
                    </div>

                    <div className="mt-12 flex flex-wrap gap-2">
                        {SECTORS.map((s) => (
                            <span
                                key={s.slug}
                                className="border border-background/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-background/70"
                            >
                                {s.name}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* QUOTE FORM */}
            <section
                data-testid="quote-section"
                id="get-connected"
                className="px-6 md:px-12 lg:px-24 py-24 md:py-32"
            >
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="font-mono text-xs tracking-[0.3em] uppercase text-accent mb-4">
                            06 / Start Building
                        </div>
                        <h2 className="font-display font-black text-4xl sm:text-6xl uppercase tracking-tight leading-[0.9]">
                            Bring us
                            <br /> your project.
                        </h2>
                        <p className="mt-6 text-lg text-muted-foreground max-w-md">
                            Share the details. Our preconstruction team will follow up with a
                            project guide and next steps.
                        </p>
                        <div className="mt-10 border-l-2 border-accent pl-6">
                            <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Direct Line</div>
                            <a
                                href={`tel:${COMPANY.phone}`}
                                className="font-display font-black text-3xl uppercase tracking-tight text-foreground hover:text-accent transition-colors block mt-2"
                                data-testid="direct-line"
                            >
                                {COMPANY.phone}
                            </a>
                        </div>
                    </div>

                    <div className="md:col-span-7">
                        <QuoteForm source="homepage_quote_form" />
                    </div>
                </div>
            </section>
        </main>
    );
};

const GalleryCard = ({ project, index }) => {
    const [imgIndex, setImgIndex] = React.useState(index % project.images.length);

    React.useEffect(() => {
        if (project.images.length < 2) return;
        // Stagger start so cards don't all flip at the same instant
        const stagger = (index * 1200) % 10000;
        const start = setTimeout(() => {
            setImgIndex((i) => (i + 1) % project.images.length);
        }, stagger);
        const timer = setInterval(() => {
            setImgIndex((i) => (i + 1) % project.images.length);
        }, 10000);
        return () => {
            clearTimeout(start);
            clearInterval(timer);
        };
    }, [project.images.length, index]);

    return (
        <Link
            to="/projects"
            data-testid={`gallery-${project.id}`}
            className="group relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[3/4] overflow-hidden bg-foreground block"
        >
            {project.images.map((src, i) => (
                <img
                    key={src}
                    src={src}
                    alt={`${project.name} — view ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-out ${
                        i === imgIndex ? "opacity-90 group-hover:opacity-100" : "opacity-0"
                    }`}
                />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/30 to-transparent" />
            <div className="absolute inset-0 p-5 md:p-6 flex flex-col justify-end">
                <div className="font-display font-black text-2xl md:text-3xl uppercase tracking-tighter leading-[0.85] text-background">
                    {project.sectorLabel}
                </div>
                <div className="mt-2 flex items-end justify-between gap-3">
                    <div className="font-mono text-[10px] md:text-xs tracking-[0.15em] uppercase text-background/80">
                        {project.name.includes("miCare") ? (
                            <>
                                {project.name.split("miCare")[0]}
                                <span className="normal-case">mi</span>C<span className="normal-case">are</span>
                                {project.name.split("miCare")[1]}
                            </>
                        ) : project.name}
                    </div>
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 text-accent shrink-0" />
                </div>
            </div>
        </Link>
    );
};

const ServiceCard = ({ span, icon, title, list, tag }) => (
    <div className={`${span} bg-secondary p-8 md:p-10`}>
        <div className="flex items-center justify-between mb-8">
            <div className="text-accent">{icon}</div>
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-secondary-foreground/50">
                {tag}
            </span>
        </div>
        <h3 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">
            {title}
        </h3>
        <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {list.map((item) => (
                <li
                    key={item.title}
                    className="flex items-start gap-3 py-3 border-b border-secondary-foreground/10 text-secondary-foreground/85"
                >
                    <span className="text-accent font-mono text-xs mt-1.5">→</span>
                    <div>
                        <div className="text-sm font-semibold">{item.title}</div>
                        {item.outcome && (
                            <div className="text-xs text-secondary-foreground/60 mt-1 leading-relaxed">
                                {item.outcome}
                            </div>
                        )}
                    </div>
                </li>
            ))}
        </ul>
    </div>
);

export default Home;
